import _ from 'lodash'
import {
    ConfigObject,
    ConfigPath,
    ConfigValue,
    convertStateToDiff,
    getValueFromBase,
    getValueFromBaseIfNotInDiff
} from '../../../../../utils/deviceConfig'
import { TerminalBasicConfigState } from './TerminalBasicConfig'

// Define types for the terminal configuration sections
export interface DeviceGeneralSection {
    test_mode: ConfigValue
    lot_id: ConfigValue
}

export interface UserInterfaceSection {
    inactivity_timeout_ms: ConfigValue
    inactivity_countdown_s: ConfigValue
    inactivity_countdown_successful_page_s: ConfigValue
    overpaid_countdown_s: ConfigValue
}

export interface PrinterSection {
    remove_printer_module: ConfigValue
}

export interface CashSection {
    remove_cash_module: ConfigValue
    treasure_capacity: ConfigValue
    currency_code: ConfigValue
}

export interface CashlessSection {
    remove_cashless_module: ConfigValue
    cashless_configure_enabled: ConfigValue
    cashless_configure_job: ConfigValue
    cashless_pu: ConfigValue
    cashless_tid: ConfigValue
}

export interface BarcodeSection {
    remove_barcode_module: ConfigValue
}

export interface TerminalConfig
    extends DeviceGeneralSection, UserInterfaceSection, PrinterSection, CashSection, CashlessSection, BarcodeSection {}

// Define the paths for terminal configuration
export const PATHS_TERMINAL: Record<keyof TerminalConfig, ConfigPath> = {
    //General
    test_mode: 'global.generic_config.test_mode',
    lot_id: 'global.generic_config.lot_id',

    //User Interface
    inactivity_timeout_ms: 'gui.inactivity_timeout_ms',
    inactivity_countdown_s: 'gui.inactivity_countdown_s',
    inactivity_countdown_successful_page_s: 'gui.inactivity_countdown_successful_page_s',
    overpaid_countdown_s: 'gui.overpaid_countdown_s',
    //Printer
    remove_printer_module: 'global.modules.remove_printer_module',
    //Cash
    remove_cash_module: 'global.modules.remove_cash_module',
    treasure_capacity: 'gui.treasure_capacity',
    currency_code: 'global.generic_config.currency_code',
    //Cashless
    remove_cashless_module: 'global.modules.remove_cashless_module',
    cashless_configure_enabled: 'cashless.cashless_configure_enabled',
    cashless_configure_job: 'cashless.cashless_configure_job',
    cashless_pu: 'cashless.cashless_pu',
    cashless_tid: 'cashless.cashless_tid',
    //Barcode
    remove_barcode_module: 'global.modules.remove_barcode_module'
}

/**
 * Gets the initial device general section configuration
 * @param baseConfig - Base configuration
 * @param diffConfig - Differential configuration
 * @returns Device general section configuration
 */
export const getInitialTerminalConfig = (baseConfig: ConfigObject, diffConfig: ConfigObject): TerminalConfig => {
    const baseConfigClone = _.cloneDeep(baseConfig)
    const diffConfigClone = _.cloneDeep(diffConfig)

    const result: Partial<TerminalConfig> = {}

    // Dynamically populate the result object using the paths
    Object.entries(PATHS_TERMINAL).forEach(([key, path]) => {
        result[key as keyof TerminalConfig] = getValueFromBaseIfNotInDiff(baseConfigClone, diffConfigClone, path)
    })

    return result as TerminalConfig
}

export const getDefaultTerminalDeviceGeneralSection = (base: ConfigObject): Partial<TerminalConfig> => {
    return {
        test_mode: getValueFromBase(base, PATHS_TERMINAL.test_mode) as Partial<TerminalConfig>,
        lot_id: getValueFromBase(base, PATHS_TERMINAL.lot_id) as Partial<TerminalConfig>
    }
}

export const getDefaultTerminalUserInterfaceSection = (base: ConfigObject): Partial<TerminalConfig> => {
    return {
        inactivity_timeout_ms: getValueFromBase(base, PATHS_TERMINAL.inactivity_timeout_ms) as Partial<TerminalConfig>,
        inactivity_countdown_s: getValueFromBase(
            base,
            PATHS_TERMINAL.inactivity_countdown_s
        ) as Partial<TerminalConfig>,
        inactivity_countdown_successful_page_s: getValueFromBase(
            base,
            PATHS_TERMINAL.inactivity_countdown_successful_page_s
        ) as Partial<TerminalConfig>,
        overpaid_countdown_s: getValueFromBase(base, PATHS_TERMINAL.overpaid_countdown_s) as Partial<TerminalConfig>
    }
}

export const getDefaultTerminalPrinterSection = (base: ConfigObject): Partial<TerminalConfig> => {
    return {
        remove_printer_module: getValueFromBase(base, PATHS_TERMINAL.remove_printer_module) as Partial<TerminalConfig>
    }
}

export const getDefaultTerminalCashSection = (base: ConfigObject): Partial<TerminalConfig> => {
    return {
        remove_cash_module: getValueFromBase(base, PATHS_TERMINAL.remove_cash_module) as Partial<TerminalConfig>,
        treasure_capacity: getValueFromBase(base, PATHS_TERMINAL.treasure_capacity) as Partial<TerminalConfig>,
        currency_code: getValueFromBase(base, PATHS_TERMINAL.currency_code) as Partial<TerminalConfig>
    }
}

export const getDefaultTerminalCashlessSection = (base: ConfigObject): Partial<TerminalConfig> => {
    return {
        remove_cashless_module: getValueFromBase(
            base,
            PATHS_TERMINAL.remove_cashless_module
        ) as Partial<TerminalConfig>,
        cashless_configure_enabled: getValueFromBase(
            base,
            PATHS_TERMINAL.cashless_configure_enabled
        ) as Partial<TerminalConfig>,
        cashless_configure_job: getValueFromBase(
            base,
            PATHS_TERMINAL.cashless_configure_job
        ) as Partial<TerminalConfig>,
        cashless_pu: getValueFromBase(base, PATHS_TERMINAL.cashless_pu) as Partial<TerminalConfig>,
        cashless_tid: getValueFromBase(base, PATHS_TERMINAL.cashless_tid) as Partial<TerminalConfig>
    }
}

export const getDefaultTerminalBarcodeSection = (base: ConfigObject): Partial<TerminalConfig> => {
    return {
        remove_barcode_module: getValueFromBase(base, PATHS_TERMINAL.remove_barcode_module) as Partial<TerminalConfig>
    }
}
export type DefaultTerminalConfigSection =
    | 'generalSection'
    | 'userInterfaceSection'
    | 'printerSection'
    | 'cashSection'
    | 'cashlessSection'
    | 'barcodeSection'
export const defaultTerminalConfigFunctions: Record<
    DefaultTerminalConfigSection,
    (base: ConfigObject) => Partial<TerminalConfig>
> = {
    generalSection: getDefaultTerminalDeviceGeneralSection,
    userInterfaceSection: getDefaultTerminalUserInterfaceSection,
    printerSection: getDefaultTerminalPrinterSection,
    cashSection: getDefaultTerminalCashSection,
    cashlessSection: getDefaultTerminalCashlessSection,
    barcodeSection: getDefaultTerminalBarcodeSection
}

export const buildTerminalConfigDiffFromState = (
    state: TerminalBasicConfigState | null,
    base: ConfigObject,
    diff: ConfigObject
): ConfigObject => {
    if (!state) return {}
    return convertStateToDiff(PATHS_TERMINAL, state as unknown as Record<string, ConfigValue>, diff, base)
}

export const getStationsForSiblingLot = (diff: any): string[] => {
    const linkedLots: string[] = []

    // Dynamically populate the result object using the paths
    // @ts-ignore
    diff?.global?.stations?.forEach((station: { lot_id: string; station_name: string }) => {
        linkedLots.push(station.lot_id)
    })
    return _.uniq(linkedLots)
}
